"use client";

import React, { useEffect, useRef, useState } from "react";
import { Award, Headphones, Megaphone, PenTool, ShieldCheck, TrendingUp, Truck, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefits = [{
  title: "Trade-Specific Designs",
  description: "Custom layouts built specifically for plumbers, electricians, and construction firms to showcase expertise and build immediate trust.",
  icon: PenTool
}, {
  title: "High Conversion Focus",
  description: "Strategically placed calls-to-action and lead forms designed to turn website visitors into high-quality paying customers.",
  icon: TrendingUp
}, {
  title: "Local Australian Support",
  description: "Based in Australia, our team is available when you need us for updates, technical advice, or strategy sessions.",
  icon: Headphones
}, {
  title: "Fast Turnaround Time",
  description: "Get your professional business website live in record time without compromising on quality, security, or performance.",
  icon: Truck
}, {
  title: "Maintenance Included",
  description: "We handle all technical updates and security patches so you can focus on running your business without tech stress.",
  icon: Wrench
}, {
  title: "Secure & Reliable Hosting",
  description: "Hosting on Google Cloud ensures 99% uptime; keeping your business running even while you sleep!",
  icon: ShieldCheck
}, {
  title: "STUNNING AESTHETICS",
  description: "Stand out from local competitors with modern, professional designs that reflect the quality of your manual work.",
  icon: Award
}, {
  title: "SEO OPTIMISED CORE",
  description: "Built from the ground up to rank on Google, helping local customers find your specific services exactly when they need them.",
  icon: Megaphone
}];
export default function CoreBenefits() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="141421" id="why-businesses-choose-website-shop" className="bg-[#1a1a1c] py-32 relative overflow-hidden bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:40px_40px]">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#2ec4b6]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 max-w-[1200px] relative z-10">
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-sans text-4xl md:text-5xl font-bold text-white uppercase tracking-normal mb-6">
            Why Businesses Choose Website Shop
          </h2>
          <p className="font-sans text-lg text-[#94a3b8] max-w-2xl mx-auto">
            Experience the difference with high-performance digital solutions tailored specifically for trades and small businesses looking to dominate their local market.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <div key={i} data-index={i} className={`transition-all duration-1000 transform ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <Card className="group h-full bg-white/5 backdrop-blur-lg border-white/10 rounded-xl transition-all duration-500 ease-out hover:-translate-y-2 hover:border-[#2ec4b6] hover:shadow-[0_0_30px_rgba(46,196,182,0.15)] flex flex-col items-center text-center pt-10 pb-8 px-6">
                  <CardHeader className="p-0 mb-6 flex flex-col items-center">
                    <div className="text-[#2ec4b6] mb-6 transition-transform duration-500 group-hover:scale-110 group-hover:drop-shadow-[0_0_15px_rgba(46,196,182,0.5)]">
                      <Icon size={64} strokeWidth={1.5} />
                    </div>
                    <CardTitle className="font-sans font-bold uppercase text-white text-xl tracking-normal">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 flex-grow flex items-start">
                    <p className="font-sans text-[#94a3b8] text-base leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}